import React, { useRef, useState } from 'react';
import { apiUrl } from '../config/api';
import { EmailInput } from './EmailInput';
import { validateEmail } from '../utils/emailValidation';
import { trackFormStart, trackGenerateLead } from '../utils/analytics';

interface WaitlistFormProps {
  courseType: 'advanced' | 'premium';
  onClose: () => void;
  onSuccess: () => void;
}

export function WaitlistForm({ courseType, onClose, onSuccess }: WaitlistFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    recruiterType: '',
    fieldOfWork: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dataProtectionAccepted, setDataProtectionAccepted] = useState(false);
  const [showDataProtectionModal, setShowDataProtectionModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isEmailValid, setIsEmailValid] = useState(false);
  const formStartTracked = useRef(false);

  const courseNames = {
    advanced: 'Deep-Dive Strategies',
    premium: 'Super-Powered Recruiter Masterclass'
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate data protection acceptance
      if (!dataProtectionAccepted) {
        setError('Please accept the Data Protection Policy to continue');
        setLoading(false);
        return;
      }

      // Validate email
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) {
        setError(emailValidation.error || 'Please enter a valid email address');
        setLoading(false);
        return;
      }

      const response = await fetch(apiUrl('/api/waitlist'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          courseType,
          courseName: courseNames[courseType]
        }),
      });

      const data = await response.json();

      if (data.success) {
        trackGenerateLead(`waitlist_${courseType}`);
        onSuccess();
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="waitlist-overlay">
      <div className="waitlist-modal">
        <div className="waitlist-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {courseType === 'advanced' ? (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3h18v18H3zM7 7h10M7 11h10M7 15h10"/>
                </svg>
                Join the Waitlist
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                I am interested
              </>
            )}
          </h3>
          <button onClick={onClose} className="waitlist-close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="waitlist-content">
          <p className="waitlist-description">
            {courseType === 'advanced' 
              ? `Be the first to know when ${courseNames[courseType]} launches! We'll notify you as soon as it's available.`
              : `Express your interest in ${courseNames[courseType]}! This helps us understand demand and you'll be notified when it becomes available.`
            }
          </p>

          <form onSubmit={handleSubmit} className="waitlist-form">
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
                Your Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onFocus={() => {
                  if (!formStartTracked.current) {
                    formStartTracked.current = true;
                    trackFormStart(`waitlist_${courseType}`);
                  }
                }}
                required
                placeholder="Enter your full name"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: 'var(--raleway)'
                }}
              />
            </div>

            <EmailInput
              value={formData.email}
              onChange={(value) => setFormData(prev => ({ ...prev, email: value }))}
              onValidationChange={setIsEmailValid}
              placeholder="Enter your email address"
              required
              label="Email Address"
            />

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
                Company Name
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Enter your company name"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: 'var(--raleway)'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
                Your Position
              </label>
              <input
                type="text"
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                placeholder="e.g., Senior Recruiter, TA Manager, HR Director"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: 'var(--raleway)'
                }}
              />
            </div>

            {['advanced', 'premium'].includes(courseType) && (
              <>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
                    Are you an external or internal recruiter? *
                  </label>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="recruiterType"
                        value="external"
                        checked={formData.recruiterType === 'external'}
                        onChange={(e) => setFormData(prev => ({ ...prev, recruiterType: e.target.value }))}
                        required
                        style={{ margin: 0 }}
                      />
                      <span style={{ fontFamily: 'var(--montserrat)' }}>External Recruiter</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="recruiterType"
                        value="internal"
                        checked={formData.recruiterType === 'internal'}
                        onChange={(e) => setFormData(prev => ({ ...prev, recruiterType: e.target.value }))}
                        required
                        style={{ margin: 0 }}
                      />
                      <span style={{ fontFamily: 'var(--montserrat)' }}>Internal Recruiter</span>
                    </label>
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
                    What field do you hire for? *
                  </label>
                  <select
                    id="fieldOfWork"
                    name="fieldOfWork"
                    value={formData.fieldOfWork}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      backgroundColor: 'white',
                      fontFamily: 'var(--raleway)'
                    }}
                  >
                    <option value="">Select hiring field</option>
                    <option value="IT">IT / Technology</option>
                    <option value="Sales">Sales</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Finance">Finance / Banking</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Retail">Retail</option>
                    <option value="Education">Education</option>
                    <option value="Multilingual">Multilingual / International</option>
                    <option value="All">All fields / General</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
                Additional Message (Optional)
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us what you're most excited about or any specific needs..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: 'var(--raleway)',
                  minHeight: '80px'
                }}
              />
            </div>

            {/* Data Protection Checkbox */}
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={dataProtectionAccepted}
                  onChange={(e) => setDataProtectionAccepted(e.target.checked)}
                  style={{ marginTop: '2px', accentColor: 'var(--orange)' }}
                  required
                />
                <span>
                  I have read and accept the{' '}
                  <button
                    type="button"
                    onClick={() => setShowDataProtectionModal(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--orange)',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Data Protection Policy
                  </button>
                  {' '}*
                </span>
              </label>
            </div>

            {error && (
              <div className="waitlist-error">
                {error}
              </div>
            )}

            <div className="waitlist-actions">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Submitting...' : (courseType === 'advanced' ? 'Join Waitlist' : 'Express Interest')}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Data Protection Modal */}
      {showDataProtectionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{ margin: 0, color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
                Data Protection Policy
              </h2>
              <button
                onClick={() => setShowDataProtectionModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--gray)'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ lineHeight: '1.8', fontSize: '1rem', fontFamily: 'var(--montserrat)' }}>
              <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--gray)', fontWeight: '600' }}>
                <strong>Recberry s.r.o. Data Protection Policy</strong><br/>
                <strong>Version (Effective): October 24, 2025</strong>
              </p>
              <p style={{ marginBottom: '1rem' }}>
                Your privacy and data protection are our top priorities at Recberry s.r.o. This Data Protection Policy applies to all data collections and processing activities conducted by Recberry s.r.o., particularly through our website, email communications, and marketing services. For purposes of this Policy, any reference to "Recberry," "we," "us," or "our" means Recberry s.r.o. and its affiliates. This Policy describes how we collect, use, share, and otherwise process your personal data in compliance with the General Data Protection Regulation (GDPR) (EU Regulation 2016/679). It also outlines your choices regarding the use, access, correction, and deletion of your personal data. The use of personal data is limited to the purposes outlined herein, such as providing marketing services, sending newsletters, and contacting you as requested.
              </p>
              <p style={{ marginBottom: '1rem' }}>
                We assure you that your personal data is used solely within our company to promote our products and services through activities like newsletters and direct communications. We do not share, sell, or transfer your data to any external parties for their own purposes beyond the necessary service providers outlined in this Policy. You can unsubscribe from our communications or request data deletion at any time, and we will promptly honor such requests.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>1. Data Controller</h3>
              <p style={{ marginBottom: '1rem' }}>
                <strong>Recberry s.r.o.</strong><br/>
                IČ: 04972236 | DIČ: CZ04972236<br/>
                Tupolevova 741, 199 00 Prague 9 - Letňany, Czech Republic<br/>
                Incorporated in the Commercial Register kept by the Municipal Court in Prague, Section C, File No. 256441<br/>
                <strong>Data Protection Contact:</strong> Barbora Jensik<br/>
                <strong>Email:</strong> barbora@recberry.com
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>2. Legal Basis for Processing</h3>
              <p style={{ marginBottom: '1rem' }}>
                We process your personal data based on your explicit consent under Article 6(1)(a) of the GDPR. This consent is freely given, specific, informed, and unambiguous. You may withdraw your consent at any time without affecting the lawfulness of processing based on consent before its withdrawal. No automated individual decision-making, including profiling under Article 22 GDPR, occurs in our processing activities.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>3. Collected Personal Data and How We Use It</h3>
              <p style={{ marginBottom: '1rem' }}>
                We may collect and/or process the following categories of personal data from individuals who interact with our services, such as newsletter subscribers, website visitors, or marketing contacts:
              </p>
              
              <div style={{ 
                background: '#f8f9fa', 
                padding: '1rem', 
                borderRadius: '8px', 
                marginBottom: '1rem',
                overflowX: 'auto'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Type of Data</th>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Description</th>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Duration</th>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Entity</th>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.4rem', fontWeight: '500' }}>Identifiers</td>
                      <td style={{ padding: '0.4rem' }}>Names, email addresses, phone numbers, job titles, employers, contact details, IP addresses</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 7</td>
                      <td style={{ padding: '0.4rem' }}>Recberry s.r.o.</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 3.2.1-6</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.4rem', fontWeight: '500' }}>IT Information</td>
                      <td style={{ padding: '0.4rem' }}>IP addresses, device information, browser type, location data, cookies</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 7</td>
                      <td style={{ padding: '0.4rem' }}>Recberry s.r.o.</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 3.2.1-6</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.4rem', fontWeight: '500' }}>Special Categories</td>
                      <td style={{ padding: '0.4rem' }}>Sensitive data under Art. 9 GDPR, only if explicitly provided and necessary</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 7</td>
                      <td style={{ padding: '0.4rem' }}>Recberry s.r.o.</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 3.2.1-6</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p style={{ marginBottom: '1rem' }}>
                Personal data is collected and used for Recberry's legitimate business purposes as the data controller, including fulfilling contractual obligations and complying with legal requirements. We process data only for the purposes described in this Policy or for which we have obtained your consent. Specifically, we use personal data for:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.1.</strong> Providing marketing services, such as contacting you about our products or events.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.2.</strong> Sending newsletters and promotional materials related to our offerings.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.3.</strong> Facilitating communications and responding to inquiries.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.4.</strong> Analyzing trends to improve our services (e.g., website usage statistics).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.5.</strong> For data security and compliance purposes, including internal audits.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.6.</strong> For statistical and analytical purposes, including product enhancement, but only in aggregated or anonymized forms.</li>
              </ul>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>4. How We Use Cookies and Similar Technologies</h3>
              <p style={{ marginBottom: '1rem' }}>
                Cookies and similar technologies (e.g., pixels, web beacons) are used by Recberry and our partners to analyze trends, administer the website, track user movements, and gather demographic information. We categorize cookies as follows:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>Essential cookies:</strong> Necessary for site functionality (e.g., session management).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Analytical cookies:</strong> For performance insights (e.g., Google Analytics, anonymized data).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Marketing cookies:</strong> For personalized content delivery.</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                We obtain your consent for non-essential cookies via a consent banner on our website, in line with GDPR and ePrivacy Directive requirements. You can manage or withdraw consent at any time through browser settings or by contacting us.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>5. Access to Personal Data</h3>
              <p style={{ marginBottom: '1rem' }}>
                Upon request and within one month (extendable to two months for complex requests, as per Art. 12 GDPR), we will provide information about your personal data we hold, including confirmation of processing, categories of data, purposes, recipients, and storage period. You can request correction, amendment, deletion, or a copy of your data in a structured, commonly used format. Contact barbora@recberry.com for these requests.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>6. Data Recipients and Transfers</h3>
              <p style={{ marginBottom: '1rem' }}>
                Recipients of your personal data are limited to sub-processors providing marketing services (e.g., email platforms) under strict GDPR-compliant data processing agreements. We have no intention to transfer data to non-EU countries or international organizations without adequate safeguards, such as Standard Contractual Clauses approved by the European Commission. All transfers, if any, are documented and ensure an equivalent level of protection. We do not share data with any other third parties for their independent use.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>7. Data Retention</h3>
              <p style={{ marginBottom: '1rem' }}>
                Personal data is retained for up to 5 years from the date of collection or until you withdraw your consent, whichever occurs first. We retain data only as long as necessary for the purposes for which it was collected, to comply with legal obligations (e.g., tax or accounting requirements), resolve disputes, or enforce agreements. Upon fulfillment of these purposes, we securely delete or anonymize the data.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>8. Data Security</h3>
              <p style={{ marginBottom: '1rem' }}>
                We employ reasonable and appropriate security measures to protect against loss, misuse, unauthorized access, disclosure, alteration, or destruction of personal data. These include:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>Technical measures:</strong> Encryption of data in transit and at rest, firewalls, secure servers.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Organizational measures:</strong> Access controls (role-based), employee training on GDPR compliance, confidentiality agreements.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Physical measures:</strong> Secure facilities for any physical records.</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                You are responsible for keeping any credentials secure. No method of transmission or storage is 100% secure; however, we regularly review and update our measures to align with industry standards.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>9. Links to Third-Party Sites</h3>
              <p style={{ marginBottom: '1rem' }}>
                Our website may contain links to third-party sites whose privacy practices differ from ours. If you submit personal data to these sites, it is governed by their policies, and we are not responsible for their actions. We encourage you to review third-party privacy policies before interacting.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>10. No Services for Minors</h3>
              <p style={{ marginBottom: '1rem' }}>
                Our services are not directed to individuals under 16 years of age. We do not knowingly collect personal data from minors. If we become aware of such collection, we will take steps to delete the data promptly.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>11. Your Rights Under GDPR</h3>
              <p style={{ marginBottom: '1rem' }}>
                In accordance with GDPR, you have the following rights regarding your personal data:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Access (Art. 15):</strong> Obtain confirmation of processing and a copy of your data.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Rectification (Art. 16):</strong> Correct inaccurate or incomplete data.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Erasure ("Right to be Forgotten," Art. 17):</strong> Request deletion when data is no longer necessary or consent is withdrawn.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Restriction of Processing (Art. 18):</strong> Limit processing under certain conditions.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Object (Art. 21):</strong> Object to processing for direct marketing or based on legitimate interests.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Data Portability (Art. 20):</strong> Receive your data in a structured, machine-readable format.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Withdraw Consent (Art. 7):</strong> Revoke consent at any time, without affecting prior lawfulness.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right Not to Be Subject to Automated Decisions (Art. 22):</strong> Not applicable, as we do not engage in such processing.</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                To exercise these rights, contact barbora@recberry.com. We will respond within one month. You also have the right to lodge a complaint with a supervisory authority, such as the Czech Office for Personal Data Protection (Úřad pro ochranu osobních údajů, www.uoou.cz).
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>12. GDPR Compliance Checklist</h3>
              <p style={{ marginBottom: '1rem' }}>
                To demonstrate our commitment to GDPR compliance, we maintain the following practices:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>Lawfulness, Fairness, and Transparency:</strong> All processing is based on consent and clearly explained in this Policy.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Purpose Limitation:</strong> Data is used only for specified marketing purposes related to our products.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Data Minimization:</strong> We collect only necessary data (e.g., name and email for newsletters).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Accuracy:</strong> We update data upon request and verify sources.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Storage Limitation:</strong> Retention is limited to 5 years or less, with regular reviews.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Integrity and Confidentiality:</strong> Security measures protect against breaches; incidents are reported within 72 hours to authorities if required (Art. 33).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Accountability:</strong> We maintain records of processing activities (Art. 30), conduct data protection impact assessments for high-risk activities (Art. 35), and appoint a data protection contact (Barbora Jensik).</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                We conduct annual internal audits to ensure ongoing compliance.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>13. Changes to This Policy</h3>
              <p style={{ marginBottom: '1rem' }}>
                We may update this Policy to reflect changes in our practices or legal requirements. Material changes will be notified via email or a prominent notice on our website at least 30 days before they take effect. We encourage you to review this Policy periodically.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>14. Data Transfers Outside the EEA</h3>
              <p style={{ marginBottom: '1rem' }}>
                Personal data is processed within the EEA. If any transfers outside the EEA are necessary (e.g., for sub-processors), we use Standard Contractual Clauses or other approved mechanisms to ensure adequate protection, as required by Chapter V of the GDPR.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>15. Compliance and Supervisory Authority</h3>
              <p style={{ marginBottom: '1rem' }}>
                We comply fully with GDPR and are registered with the relevant authorities. You have the right to lodge a complaint with the Czech Office for Personal Data Protection (www.uoou.cz) if you believe your rights have been infringed.
              </p>
              
              <div style={{ 
                background: 'var(--light-gray)', 
                padding: '1.5rem', 
                borderRadius: '8px',
                marginTop: '1.5rem'
              }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>16. Data Protection Contact</h3>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                  For any questions, concerns, or to exercise your rights, contact:<br/>
                  <strong>Barbora Jensik</strong><br/>
                  <strong>Email:</strong> barbora@recberry.com<br/>
                  <strong>Address:</strong> Recberry s.r.o., Tupolevova 741, 199 00 Prague 9 - Letňany, Czech Republic
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
